import * as THREE from 'three';

const DEFAULT_MARGIN = 1.16;
const CLIP_LIMIT = 0.96;

function boxCorners(box) {
  const corners = [];
  for (const x of [box.min.x, box.max.x]) {
    for (const y of [box.min.y, box.max.y]) {
      for (const z of [box.min.z, box.max.z]) corners.push(new THREE.Vector3(x, y, z));
    }
  }
  return corners;
}

export function perspectiveDistanceToFitBox(camera, box, direction, margin = DEFAULT_MARGIN) {
  if (box.isEmpty()) return 0;
  const center = box.getCenter(new THREE.Vector3());
  const viewDirection = direction.clone().normalize();
  const right = new THREE.Vector3().crossVectors(camera.up, viewDirection);
  if (right.lengthSq() < 1e-8) right.set(1, 0, 0);
  else right.normalize();
  const up = new THREE.Vector3().crossVectors(viewDirection, right).normalize();
  const verticalTangent = Math.tan(THREE.MathUtils.degToRad(camera.fov / 2));
  const horizontalTangent = verticalTangent * Math.max(camera.aspect, 0.01);

  return Math.max(...boxCorners(box).map((corner) => {
    const offset = corner.sub(center);
    const depthOffset = offset.dot(viewDirection);
    const horizontalDistance = Math.abs(offset.dot(right)) / horizontalTangent;
    const verticalDistance = Math.abs(offset.dot(up)) / verticalTangent;
    return depthOffset + margin * Math.max(horizontalDistance, verticalDistance);
  }));
}

export function cameraContainsBox(camera, box, limit = CLIP_LIMIT) {
  if (box.isEmpty()) return true;
  camera.updateMatrixWorld(true);
  return boxCorners(box).every((corner) => {
    const projected = corner.project(camera);
    return projected.z >= -1
      && projected.z <= 1
      && Math.abs(projected.x) <= limit
      && Math.abs(projected.y) <= limit;
  });
}

export function fitCameraToBox({
  camera,
  controls,
  box,
  defaultPosition,
  baseMaxDistance,
  preserveDirection = false,
  margin = DEFAULT_MARGIN,
}) {
  const center = box.getCenter(new THREE.Vector3());
  const currentDirection = camera.position.clone().sub(controls.target);
  const direction = preserveDirection && currentDirection.lengthSq() > 1e-8
    ? currentDirection.normalize()
    : defaultPosition.clone().normalize();
  const fitDistance = perspectiveDistanceToFitBox(camera, box, direction, margin);
  const distance = Math.max(defaultPosition.length(), fitDistance);

  controls.target.copy(center);
  controls.maxDistance = Math.max(baseMaxDistance, distance * 1.65);
  camera.position.copy(center).addScaledVector(direction, distance);
  camera.far = Math.max(camera.far, distance + box.getSize(new THREE.Vector3()).length() * 4);
  camera.lookAt(center);
  camera.updateProjectionMatrix();
  controls.update();
  return distance;
}
